import events from "_src/engine/events";
import DefaultShader from "_src/shaders/default/default_shader";
import primitiveFactory from "_src/engine/factories/primitive";
import objFactory from "_src/engine/factories/object";
import output from "_src/engine/output";
import {primitiveType, geometry} from "_src/engine/constants";
import {centroid, planeODR} from "_src/engine/math";

import AbstractManipulator from "_src/client/manipulators/abstract_manipulator";

import {vec3, mat4, quat} from "_src/libs/gl-matrix";
import { screenUtils } from "_src/client/utils";
import {shadingType, basePlane, stepType} from "_src/constants";
import history from "_src/history/manager";
import app_state from "_src/app_state";
import workspace from "_src/workspace";
import windowManager from "_src/client/window_manager";
import preview from "_src/client/preview";

const _VEC3_TMP = vec3.create();
const _VEC3_TMP2 = vec3.create();
const _VEC3_TMP3 = vec3.create();
const _VEC3_TMP4 = vec3.create();

const _MAT4_TMP = mat4.create();
const _MAT4_TMP2 = mat4.create();
const _MAT4_TMP3 = mat4.create();

const _QUAT_TMP = quat.create();

const SPHERE_SIZE = 10;
const SPHERE_COLOR = [0.5, 0.0, 0.5];
const COLINEAR_EPSILON = 0.99999;

export default class Positioner extends AbstractManipulator {
  constructor(camera) {

    super(camera);

    this._cameraMode = false;

    this._sphereGeometry = primitiveFactory.create(primitiveType.SPHERE);
    this._sphereShader = new DefaultShader({
      shading: shadingType.SMOOTH,
      specularColor: [0,0,0]
    });

    this._picks = [];
    this._activePreviews = [];
    this._transformMatrix = null;
    this._plane = basePlane.XOY;
    this._planeNormalInverted = false;

  }

  _preview(enabled) {
    this._updateTransformMatrix();

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const mainRenderGraph = mainWindow.renderGraph;
    const workspaceBlendingScene = mainRenderGraph.getPostBlendingInterfaceScene();

    this._activePreviews.forEach((object) => {
      mainWindow.removeObject(object);
      workspaceBlendingScene.remove(object.model);
    }, workspaceBlendingScene);
    this._activePreviews.length = 0;

    if (!enabled || !this._transformMatrix) {
      return;
    }

    for (const object of workspace.getObjects()) {

      const previewObject = preview.createPreview(object, this._transformMatrix);

      //order does matter. First add to scene, then - to window
      workspaceBlendingScene.add(previewObject.model);
      mainWindow.addObject(previewObject);
      this._activePreviews.push(previewObject);
    }

    events.emit("object_bboxes_updated", this._activePreviews);

    mainRenderGraph.requestFrame();
  }

  invertPlaneNormal(inverted) {
    this._planeNormalInverted = inverted;
    this._preview(true);
  }

  setPlane(planeEnum) {
    this._plane = planeEnum;
    this._preview(true);
  }

  apply() {
    const objects = workspace.getObjects().filter(object => object.getVisibility() && !object.getLockState());
    const res = this._cameraMode ? this._applyByCamera(this._plane, objects) : this._applyByPoints(this._plane, objects);
    if (res) {
      for (const object of objects) {
        object.move(object.model.modelMatrix, true);
        events.emit("object_moved", object);
      }
      events.emit("objects_updated", objects);
    }
    return res;
  }

  setCameraModeEnabled(enabled) {
    if (this._cameraMode === enabled) {
      return;
    }

    if (!this._cameraMode) {
      this.reset();
    }

    this._cameraMode = enabled;
  }

  reset() {
    const interfaceScene = windowManager.getWindow(windowManager.MAIN).renderGraph.getInterfaceScene();
    for (const pick of this._picks) {
      interfaceScene.remove(pick);
    }
    this._picks.length = 0;
    this._preview(false);
    this._reportPointsCount();
  }

  update() {
    const enabled = Boolean(this._activePreviews.length);
    this._preview(enabled);
  }

  _reportPointsCount() {
    app_state.setStateValue("positionerPointsCount", this._picks.length);
  }

  _applyByCamera(planeEnum, objects){
    const cameraControl = windowManager.getWindow(windowManager.MAIN).cameraControl;
    const camera = cameraControl.object;

    const minmax = workspace.getBBox((object) => { return objects.indexOf(object) > -1; });
    const origin = vec3.set(_VEC3_TMP, 0.5 * (minmax[0] + minmax[3]),
                                       0.5 * (minmax[1] + minmax[4]),
                                       0.5 * (minmax[2] + minmax[5]));
    const toCamera = vec3.sub(_VEC3_TMP2, camera.trans, origin);
    const toViewBefore = mat4.copy(_MAT4_TMP, camera.viewMat);
    const view = vec3.set(_VEC3_TMP3, 0, 0, -1);
    vec3.transformQuat(view, view, camera.quat);

    let viewAfter;
    switch (planeEnum) {
      case basePlane.XOY :
        viewAfter = geometry.AXIS_MZ;
        break;
      case basePlane.YOZ :
        viewAfter = geometry.AXIS_MX;
        break;
      case basePlane.ZOX :
        viewAfter = geometry.AXIS_MY;
        break;
      default :
        output.error(output.API_ERROR, "Can't apply positioning. Wrong positioning plane: \"" + planeEnum + "\"");
        return false;
    }
    const rotation = quat.rotationTo(_QUAT_TMP, view, viewAfter);
    const toCameraAfter = vec3.transformQuat(_VEC3_TMP2, toCamera, rotation);
    const height = Math.abs(vec3.dot(viewAfter, toCameraAfter));
    const pos = vec3.scale(_VEC3_TMP, viewAfter, -height);

    cameraControl.lookAt(0, 0, 0);
    cameraControl.setPosition(pos);
    cameraControl.updateRotation();
    camera.updateMatrix();

    const toWorldAfter = mat4.copy(_MAT4_TMP2, camera.modelMatrix);
    const transform = mat4.multiply(_MAT4_TMP3, toWorldAfter, toViewBefore);

    for (let object of objects) {
      history.writeObjectToCurrentStep(object);
      this._applyTransform(object.model, transform);
    }
    events.emit("camera_move", camera);
    history.addStep(stepType.TRANSFORM);

    if (this._picks.length > 0) {
      output.warn("Picks list is not empty when applying positioning by camera");
      this.reset();
    }
    return true;
  }

  _applyByPoints(planeEnum, objects){
    const transform = this._transformMatrix;
    if (!transform) {
      output.error(output.API_ERROR, "Can't apply positioning. Not enough points to positioning.");
      return false;
    }

    for (let object of objects) {
      history.writeObjectToCurrentStep(object);
      this._applyTransform(object.model, transform);
    }

    for (const pick of this._picks) {
      this._applyTransform(pick, transform);
    }

    history.addStep(stepType.TRANSFORM);

    this._preview(false);

    return true;
  }

  _updateTransformMatrix() {
    const normal = this._getPlaneNormal(this._plane, _VEC3_TMP3);
    if (!normal) {
      output.error(output.API_ERROR, "Can't apply positioning. Wrong position plane: \"" + this._plane + "\"");
      this._transformMatrix = null;
      return;
    }
    if (this._planeNormalInverted) {
      vec3.negate(normal, normal);
    }

    const points = this._getPoints();
    if (!points) {
      this._transformMatrix = null;
      return;
    }

    this._transformMatrix = this._getTransform(points, normal, _MAT4_TMP3);
  }

  _mouseUpHandler({screenX, screenY}) {

    if (this._cameraMode) {
      return;
    }

    this._pickHandler(screenX, screenY).then(sphere => {
      if (!sphere) {
        return;
      }

      const interfaceScene = windowManager.getWindow(windowManager.MAIN).renderGraph.getInterfaceScene();
      this._picks.push(sphere);
      interfaceScene.add(sphere);
      this._preview(true);
      this._reportPointsCount();
    });
  }

  _cameraMoveHandler(camera) {
    if (camera !== this._camera) {
      return;
    }

    this._updateScales(this._camera);
  }

  _onStart() {
    this._cameraMode = false;
    this.reset();
  }

  _onStop() {
    this.reset();
  }

  async _pickHandler(screenX, screenY) {

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const camera = workspace.getScene().camera;

    const sphereCenter = _VEC3_TMP;
    const res = await mainWindow.getPixelWorldPoint(screenX, screenY, camera, sphereCenter);
    if (!res) {
      return null;
    }

    const drawModel = this._createDrawModel(sphereCenter);
    return drawModel;
  }

  _resizeHandler() {
    this._updateScales(this._camera);
  }

  _createDrawModel(position) {
    const drawModel = objFactory.createDrawable(this._sphereShader);
    drawModel.addGeometryChunk(this._sphereGeometry);
    drawModel.setTranslation(position[0], position[1], position[2]);
    drawModel.setDiffuseColor(SPHERE_COLOR[0], SPHERE_COLOR[1], SPHERE_COLOR[2]);
    this._updateScale(drawModel, this._camera);
    drawModel.updateMatrix();
    return drawModel;
  }

  _undoHandler() {
    this.reset();
  }

  _redoHandler() {
    this.reset();
  }

  _updateScale(object, camera) {
    const scale = screenUtils.calculateObjectScale(SPHERE_SIZE, 2, object.trans, camera);
    object.setScale(scale, scale, scale);
  }

  _updateScales(camera) {
    for (const pick of this._picks) {
      const scale = screenUtils.calculateObjectScale(SPHERE_SIZE, 2, pick.trans, camera);
      pick.setScale(scale, scale, scale);
    }
  }

  _getTransform(points, normal, transform) {
    const _keep_origin = false;

    const position = centroid(points, _VEC3_TMP);
    const direction = planeODR(points, position, _VEC3_TMP2);
    if (!direction) {
      return mat4.fromTranslation(transform, vec3.negate(_VEC3_TMP4, position));
    }

    vec3.normalize(direction, direction);

    let maxId;
    let maxVal = -Infinity;
    for (let i = 0; i<3; i++){
      if (Math.abs(direction[i]) > maxVal) {
        maxVal = direction[i];
        maxId = i;
      }
    }
    if (direction[maxId] < 0) {
      vec3.negate(direction, direction);
    }

    if (Math.abs(vec3.dot(normal, direction)) > COLINEAR_EPSILON){
      return mat4.identity(_MAT4_TMP);
    }

    let translate = _keep_origin ?
      mat4.fromTranslation(_MAT4_TMP, vec3.scale(_VEC3_TMP4, direction, -vec3.dot(position, direction))) :
      mat4.fromTranslation(_MAT4_TMP, vec3.negate(_VEC3_TMP4, position));

    const rotation = quat.rotationTo(_QUAT_TMP, direction, normal);
    const rotate = mat4.fromQuat(_MAT4_TMP2, rotation);

    return mat4.multiply(transform, rotate, translate);
  }

  _getPoints() {
    if (this._picks.length < 3) {
      return null;
    }

    const points = [];
    for (const pick of this._picks){
      const translation = pick.getTranslation(_VEC3_TMP);
      points.push([translation[0], translation[1], translation[2]]);
    }
    return points;
  }

  _getPlaneNormal(planeEnum, normal){
    switch (planeEnum) {
      case basePlane.XOY :
        vec3.set(normal, 0,0,1);
        break;
      case basePlane.YOZ :
        vec3.set(normal, 1,0,0);
        break;
      case basePlane.ZOX :
        vec3.set(normal, 0,1,0);
        break;
      default :
        return null;
    }
    return normal;
  }

  _applyTransform(objModel, transform) {
    const objMatrix = objModel.modelMatrix;
    mat4.multiply(objMatrix, transform, objMatrix);
    objModel.setMatrix(objMatrix);
  }

}
